// ══════════════════════════════════════════════════════════════════
//  Queries de configuração (TanStack Query).
//
//  PADRÃO DE QUERY (referência para os demais):
//   - Envolve um repository com useQuery/useMutation.
//   - Usa as chaves de keys.ts.
//   - Configs "frias" (mudam raramente) usam staleTime alto: a
//     revalidação acontece via invalidateQueries no save, não por
//     refetch automático. Isso mata o refetch repetido em 5-6 modais.
// ══════════════════════════════════════════════════════════════════

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { qk } from './keys';
import * as configRepository from '@app';
import type { CommissionRates } from '@/types/domain';

const HORA = 60 * 60 * 1000;

/** Taxas de comissão do workspace (config.commission_rates). */
export function useCommissionRates(workspaceId?: string) {
  return useQuery({
    queryKey: qk.config.commissionRates(workspaceId ?? ''),
    queryFn: () => configRepository.getCommissionRates(workspaceId as string),
    enabled: !!workspaceId,
    staleTime: HORA,
    gcTime: 2 * HORA,
  });
}

/** Salva as taxas e invalida o cache para revalidar. */
export function useSetCommissionRates(workspaceId?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (rates: CommissionRates) =>
      configRepository.setCommissionRates(workspaceId as string, rates),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.config.commissionRates(workspaceId ?? '') });
    },
  });
}

/** Canais e origens canônicos do workspace (config.workspace_origens). */
export function useWorkspaceOrigins(workspaceId?: string) {
  return useQuery({
    queryKey: qk.config.workspaceOrigins(workspaceId ?? ''),
    queryFn: () => configRepository.getWorkspaceOrigins(workspaceId as string),
    enabled: !!workspaceId,
    staleTime: HORA,
    gcTime: 2 * HORA,
  });
}

export function useWorkspaceOriginCounts(workspaceId?: string) {
  return useQuery({
    queryKey: qk.config.workspaceOriginCounts(workspaceId ?? ''),
    queryFn: () => configRepository.getWorkspaceOriginCounts(workspaceId as string),
    enabled: !!workspaceId,
    staleTime: HORA,
    gcTime: 2 * HORA,
  });
}

/** Salva canais/origens (reusa a normalização v4 do service) e invalida o cache. */
export function useSetWorkspaceOrigins(workspaceId?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { canais: any[]; origens: any[] }) =>
      configRepository.setWorkspaceOrigins(workspaceId as string, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.config.workspaceOrigins(workspaceId ?? '') });
      qc.invalidateQueries({ queryKey: qk.config.workspaceOriginCounts(workspaceId ?? '') });
    },
  });
}
